import { useTranslation } from 'react-i18next';

import { FullScreenNotice } from './FullScreenNotice';
import { RotateCwIcon } from '../../../components';

/**
 * 한 진단을 마쳤다고 알리는 전체화면 (시안 `884:9451`).
 *
 * **다이얼로그가 아니라 화면이다.** 진단과 진단 사이의 쉼표라, 뒤에 방금 잰 화면이
 * 비쳐 보이면 아직 그 진단 안에 있는 것처럼 읽힌다. 세그먼트 사이(저음 → 고음)는
 * 여전히 작은 다이얼로그다 — 그건 같은 진단 안의 이동이라 화면을 갈아탈 일이 아니다.
 *
 * 시안에 없지만 **측정 요약을 남긴다**: 값이 없으면 `재 측정`을 누를지 말지를 찍어서
 * 정해야 한다. 다시 해도 이전 시도는 안 버린다(MPT·음역은 시도들의 최댓값을 쓴다).
 */

interface StepDoneViewProps {
  /** 1-기준 진단 번호 — 시안의 `1단계`. */
  step: number;
  /** 방금 마친 진단 이름. */
  testTitle: string;
  /** 다음이 있으면 이어서 잰다고, 없으면 결과를 만든다고 말한다. */
  hint: string;
  /** 방금 시도 요약(예: `12.4초 발성`, `최저 E2`). */
  summary: string;
  /** 이 시도로 지표가 안 나올 것 같을 때의 안내. 전진을 막지는 않는다. */
  warning: string | null;
  /** 몇 번째 시도인지 — 2회차부터만 보인다. */
  attemptCount: number;
  onRetry: () => void;
  onNext: () => void;
  /**
   * 마감이 도는 중 — 마지막 진단에서 "다음"을 누르면 녹음 업로드를 거두느라 잠깐 걸린다.
   * 그동안 두 버튼은 눌러도 아무 일이 없으므로(훅이 무시한다) 눌리지 않는 것을 보여 준다.
   */
  busy?: boolean;
}

export const StepDoneView = ({
  step,
  testTitle,
  hint,
  summary,
  warning,
  attemptCount,
  onRetry,
  onNext,
  busy = false,
}: StepDoneViewProps) => {
  const { t } = useTranslation();

  // 경고가 붙은 시도에서는 **권장 행동이 뒤집힌다.** 검정 채움은 "이걸 누르세요"라는
  // 뜻이라, 못 쓸 가능성이 있는 시도 위에서 `다음`이 채워져 있으면 화면이 잘못된 쪽을
  // 가리킨다. 전진을 막지는 않는다 — 강조만 옮긴다.
  const solid =
    'h-[50px] flex-1 rounded-full bg-[var(--bk-900)] text-base font-bold tracking-[0.08px] text-white disabled:opacity-50 motion-safe:active:scale-95';
  const outline =
    'h-[50px] flex-1 rounded-full border-2 border-[hsl(var(--field-border))] text-base font-bold tracking-[0.08px] text-[var(--bk-900)] disabled:opacity-50 motion-safe:active:scale-95';

  return (
    <FullScreenNotice
      title={t('web.diagnosis.stepDone.title', { step, test: testTitle })}
      banner={warning ? t('web.diagnosis.stepDone.warnHint') : hint}
      showMascot={!warning}
      footer={
        <>
          {/* 시안의 하단 버튼 둘 — 기본은 `재 측정`(아웃라인) / `다음`(검정). */}
          <button
            type="button"
            onClick={onRetry}
            disabled={busy}
            className={`flex items-center justify-center gap-2 ${warning ? solid : outline}`}
          >
            <RotateCwIcon className="h-4 w-4" aria-hidden="true" />
            {t('web.diagnosis.stepDone.retry')}
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={busy}
            aria-busy={busy}
            className={warning ? outline : solid}
          >
            {/* 업로드를 거두는 동안 화면이 죽은 것처럼 보이지 않게 말로 바꾼다. */}
            {busy
              ? t('web.diagnosis.stepDone.finishing')
              : t('web.diagnosis.stepDone.next')}
          </button>
        </>
      }
    >
      {/* 잰 값. 시안에는 없는 줄이지만 `재 측정` 판단의 유일한 근거다. */}
      <p className="mt-4 text-center text-sm font-semibold tabular-nums text-[var(--bk-600)]">
        {summary}
      </p>
      {warning && (
        <p className="mt-1 text-center text-xs text-destructive">{warning}</p>
      )}
      {attemptCount > 1 && (
        <p className="mt-1 text-center text-xs text-muted-foreground">
          {t('web.diagnosis.review.attempts', { value: attemptCount })}
        </p>
      )}
    </FullScreenNotice>
  );
};
